<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();

const useDefaultData = ref(true);
const viewMode = ref('grid');
const selectedCountry = ref('');
const selectedLanguage = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const lastSavedDraftSnapshot = ref('');

// Note: media "default" mode is controlled by filtering on media.use_default_data, not product.use_default_data.

const assistantRecord = computed(() =>
  store.getters?.['ailabAssistants/getRecord']
    ? store.getters['ailabAssistants/getRecord'](Number(props.assistantId))
    : null
);

const isAIGamer = computed(
  () => assistantRecord.value?.config?.wlab_sidebar_key === 'ai_gamer'
);

// Modal state
const showModal = ref(false);
const editingItem = ref(null);
const formData = ref({
  folder_name: '',
  title: '',
  description: '',
  media_type: 'image',
  url: '',
  country: '',
  language: '',
});

const snapshotDraft = () =>
  JSON.stringify({
    folder_name: formData.value.folder_name || '',
    title: formData.value.title || '',
    description: formData.value.description || '',
    media_type: formData.value.media_type || 'image',
    url: formData.value.url || '',
    country: formData.value.country || '',
    language: formData.value.language || '',
  });

const markDraftSnapshot = () => {
  lastSavedDraftSnapshot.value = snapshotDraft();
};

const countryOptions = computed(() => [
  {
    value: '',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.ALL'),
  },
  {
    value: 'MY',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.MY'),
  },
  {
    value: 'ID',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.ID'),
  },
  {
    value: 'SG',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.SG'),
  },
  {
    value: 'US',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.US'),
  },
  {
    value: 'TH',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.TH'),
  },
]);

const languageOptions = computed(() => [
  {
    value: '',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.ALL'),
  },
  {
    value: 'en',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.EN'),
  },
  {
    value: 'zh',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.ZH'),
  },
  {
    value: 'ms',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.MS'),
  },
  {
    value: 'th',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.TH'),
  },
]);

const mediaTypeOptions = computed(() => [
  {
    value: 'image',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MEDIA_TYPE.IMAGE'),
  },
  {
    value: 'video',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MEDIA_TYPE.VIDEO'),
  },
]);

// Get media from store
const storeMedia = computed(() => store.getters['aiCloser/getMedia']);
const storeMediaMeta = computed(() => store.getters['aiCloser/getMediaMeta']);

// Local reactive media items
const mediaItems = ref([]);
const pageLimit = 25;
const currentOffset = ref(0);
const totalCount = ref(0);

// Fetch media on mount
onMounted(async () => {
  if (!props.assistantId) return;
  // AI gamer defaults to viewing "default" library first.
  if (isAIGamer.value) useDefaultData.value = true;
  await handleRefresh();
});

watch(
  () => useDefaultData.value,
  () => handleRefresh()
);

// Sync store media to local
watch(
  () => storeMedia.value,
  newVal => {
    mediaItems.value = (newVal || []).map(m => ({
      id: m.id,
      folder: m.folder_name || '',
      title: m.title || '',
      description: m.description || '',
      type: m.media_type || 'image',
      url: m.url || '',
      country: m.country || '',
      language: m.language || '',
      createdAt: m.created_at ? new Date(m.created_at).toLocaleString() : '',
      updatedAt: m.updated_at ? new Date(m.updated_at).toLocaleString() : '',
    }));
  },
  { immediate: true, deep: true }
);

watch(
  () => storeMediaMeta.value,
  meta => {
    totalCount.value = meta?.total ?? mediaItems.value.length;
  },
  { immediate: true, deep: true }
);

const filteredMedia = computed(() => {
  // Server-side filtering handles country/language; keep this as a no-op for safety.
  return mediaItems.value;
});

const handleRefresh = async () => {
  if (!props.assistantId) return;
  isLoading.value = true;
  try {
    currentOffset.value = 0;
    await store.dispatch('aiCloser/fetchMedia', {
      assistantId: props.assistantId,
      params: {
        use_default_data: useDefaultData.value,
        country: selectedCountry.value || undefined,
        language: selectedLanguage.value || undefined,
        limit: pageLimit,
        offset: 0,
      },
    });
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

const canLoadMore = computed(
  () => mediaItems.value.length < (totalCount.value || 0)
);

const handleLoadMore = async () => {
  if (!props.assistantId || isLoading.value) return;
  if (!canLoadMore.value) return;
  isLoading.value = true;
  try {
    const nextOffset = currentOffset.value + pageLimit;
    const response = await store.dispatch('aiCloser/fetchMedia', {
      assistantId: props.assistantId,
      params: {
        use_default_data: useDefaultData.value,
        country: selectedCountry.value || undefined,
        language: selectedLanguage.value || undefined,
        limit: pageLimit,
        offset: nextOffset,
      },
    });
    // Merge results (store replaces); local is derived from store, so do manual merge here for UX.
    const items = response?.data || [];
    if (Array.isArray(items) && items.length) {
      const byId = new Map(mediaItems.value.map(m => [m.id, m]));
      items.forEach(m => {
        const mapped = {
          id: m.id,
          folder: m.folder_name || '',
          title: m.title || '',
          description: m.description || '',
          type: m.media_type || 'image',
          url: m.url || '',
          country: m.country || '',
          language: m.language || '',
          createdAt: m.created_at
            ? new Date(m.created_at).toLocaleString()
            : '',
          updatedAt: m.updated_at
            ? new Date(m.updated_at).toLocaleString()
            : '',
        };
        byId.set(mapped.id, mapped);
      });
      mediaItems.value = Array.from(byId.values());
      currentOffset.value = nextOffset;
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.LOAD_MORE_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

// Modal handlers
const openAddModal = () => {
  editingItem.value = null;
  formData.value = {
    folder_name: isAIGamer.value ? 'Promo_Banners' : '',
    title: '',
    description: '',
    media_type: 'image',
    url: '',
    country: isAIGamer.value ? 'TH' : '',
    language: isAIGamer.value ? 'th' : '',
  };
  markDraftSnapshot();
  showModal.value = true;
};

const openEditModal = item => {
  editingItem.value = item;
  formData.value = {
    folder_name: item.folder,
    title: item.title,
    description: item.description,
    media_type: item.type,
    url: item.url,
    country: item.country,
    language: item.language,
  };
  markDraftSnapshot();
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingItem.value = null;
};

const handleSave = async () => {
  if (!props.assistantId) return;
  if (!formData.value.title || !formData.value.url) {
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.TITLE_URL_REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    if (editingItem.value) {
      await store.dispatch('aiCloser/updateMedia', {
        assistantId: props.assistantId,
        mediaId: editingItem.value.id,
        data: formData.value,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.UPDATED'));
    } else {
      await store.dispatch('aiCloser/createMedia', {
        assistantId: props.assistantId,
        data: formData.value,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.ADDED'));
    }
    markDraftSnapshot();
    closeModal();
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async item => {
  if (
    !confirm(
      t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.DELETE_CONFIRM', {
        title: item?.title || '',
      })
    )
  )
    return;

  try {
    await store.dispatch('aiCloser/deleteMedia', {
      assistantId: props.assistantId,
      mediaId: item.id,
    });
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.DELETED'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ALERTS.DELETE_FAILED')
    );
  }
};

const isFormValid = computed(() => {
  return formData.value.title && formData.value.url;
});

const canSaveDraft = computed(
  () =>
    Boolean(
      props.assistantId &&
        showModal.value &&
        isFormValid.value &&
        !isSaving.value &&
        snapshotDraft() !== lastSavedDraftSnapshot.value
    )
);

defineExpose({
  save: handleSave,
  canSave: () => canSaveDraft.value,
});
</script>

<template>
  <div class="media-tab">
    <!-- Toggle Switch -->
    <div class="toggle-row">
      <button
        class="crt-switch"
        :class="{ 'is-on': useDefaultData }"
        type="button"
        @click="useDefaultData = !useDefaultData"
      >
        <span />
      </button>
      <span class="toggle-label">
        {{
          isAIGamer
            ? t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.TOGGLE.LABEL_AIGAMER')
            : t('AILAB.ASSISTANT_V2.PRODUCT_INFO.TOGGLE.LABEL_DEFAULT')
        }}
      </span>
    </div>

    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <select v-model="selectedCountry" class="filter-select">
          <option
            v-for="opt in countryOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <select v-model="selectedLanguage" class="filter-select">
          <option
            v-for="opt in languageOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <button
          class="refresh-btn"
          :disabled="isLoading"
          @click="handleRefresh"
        >
          <span
            class="i-lucide-refresh-cw size-4"
            :class="[isLoading && 'animate-spin']"
          />
        </button>
        <span class="toggle-label" style="opacity: 0.9">
          {{ mediaItems.length }} / {{ totalCount }}
        </span>
      </div>
      <div class="toolbar-right">
        <button
          v-if="canLoadMore"
          class="add-btn"
          :disabled="isLoading"
          @click="handleLoadMore"
        >
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ACTIONS.LOAD_MORE') }}
        </button>
        <button
          class="add-btn"
          :disabled="useDefaultData"
          @click="openAddModal"
        >
          <span class="i-lucide-plus size-4" />
          <span>
            {{
              isAIGamer
                ? t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ACTIONS.ADD_AIGAMER')
                : t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ACTIONS.ADD_DEFAULT')
            }}
          </span>
        </button>
        <div class="view-toggle">
          <button
            class="view-btn"
            :class="[viewMode === 'list' && 'active']"
            @click="viewMode = 'list'"
          >
            <span class="i-lucide-list size-4" />
          </button>
          <button
            class="view-btn"
            :class="[viewMode === 'grid' && 'active']"
            @click="viewMode = 'grid'"
          >
            <span class="i-lucide-layout-grid size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Media Container -->
    <div class="media-container" :class="{ 'is-default': useDefaultData }">
      <!-- Default Watermark -->
      <div v-if="useDefaultData" class="default-watermark">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_INFO.DEFAULT_WATERMARK') }}
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="empty-state">
        <span class="i-lucide-loader-2 size-10 animate-spin text-orange-400" />
        <p class="empty-text">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.STATE.LOADING') }}
        </p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredMedia.length === 0" class="empty-state">
        <div class="empty-icon">
          <span class="i-lucide-image size-8" />
        </div>
        <h4 class="empty-title">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.STATE.EMPTY_TITLE') }}
        </h4>
        <p class="empty-text">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.STATE.EMPTY_SUBTITLE') }}
        </p>
        <button
          v-if="!useDefaultData"
          class="add-first-btn"
          @click="openAddModal"
        >
          <span class="i-lucide-plus size-4" />
          <span>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.ACTIONS.ADD_FIRST')
          }}</span>
        </button>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="media-grid">
        <div v-for="item in filteredMedia" :key="item.id" class="media-card">
          <div class="card-header">
            <div class="folder-info">
              <span class="i-lucide-folder-open size-4" />
              <span class="folder-name">
                {{
                  item.folder ||
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED')
                }}
              </span>
            </div>
            <div v-if="!useDefaultData" class="card-actions">
              <button class="action-btn edit" @click="openEditModal(item)">
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <button class="action-btn delete" @click="handleDelete(item)">
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
          <div class="card-content">
            <div class="media-preview">
              <template v-if="item.type === 'image'">
                <img :src="item.url" :alt="item.title" class="preview-image" />
              </template>
              <template v-else>
                <video :src="item.url" class="preview-video" />
                <button class="play-btn">
                  <span class="i-lucide-play size-5" />
                </button>
              </template>
            </div>
            <div class="media-info">
              <h4 class="media-title">{{ item.title }}</h4>
              <p v-if="item.description" class="media-desc">
                {{ item.description }}
              </p>
              <div class="media-dates">
                <small>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.CREATED', {
                    date: item.createdAt,
                  })
                }}</small>
                <small>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.UPDATED', {
                    date: item.updatedAt,
                  })
                }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="media-list">
        <div v-for="item in filteredMedia" :key="item.id" class="media-row">
          <div class="media-thumb">
            <template v-if="item.type === 'image'">
              <img :src="item.url" :alt="item.title" />
            </template>
            <template v-else>
              <video :src="item.url" />
              <span class="i-lucide-play size-4 play-icon" />
            </template>
          </div>
          <div class="media-details">
            <div class="folder-info">
              <span class="i-lucide-folder-open size-3.5" />
              <span>{{
                item.folder ||
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED')
              }}</span>
            </div>
            <h4 class="media-title">{{ item.title }}</h4>
            <small class="media-date">{{
              t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.UPDATED', {
                date: item.updatedAt,
              })
            }}</small>
          </div>
          <div v-if="!useDefaultData" class="row-actions">
            <button class="action-btn edit" @click="openEditModal(item)">
              <span class="i-lucide-pencil size-4" />
            </button>
            <button class="action-btn delete" @click="handleDelete(item)">
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>
              {{
                editingItem
                  ? isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.TITLE.EDIT_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.TITLE.EDIT_DEFAULT'
                      )
                  : isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.TITLE.ADD_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.TITLE.ADD_DEFAULT'
                      )
              }}
            </h3>
            <button class="close-btn" @click="closeModal">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>
                {{
                  isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.TITLE.LABEL_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.TITLE.LABEL_DEFAULT'
                      )
                }}
                <span class="required">*</span>
              </label>
              <input
                v-model="formData.title"
                type="text"
                :placeholder="
                  isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.TITLE.PLACEHOLDER_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.TITLE.PLACEHOLDER_DEFAULT'
                      )
                "
                class="form-input"
              />
              <p v-if="isAIGamer" class="field-help">
                {{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.TITLE.HELP_AIGAMER'
                  )
                }}
              </p>
            </div>
            <div class="form-group">
              <label
                >{{ t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.URL.LABEL') }}
                <span class="required">*</span></label
              >
              <input
                v-model="formData.url"
                type="url"
                :placeholder="
                  isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.URL.PLACEHOLDER_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.URL.PLACEHOLDER_DEFAULT'
                      )
                "
                class="form-input"
              />
              <p v-if="isAIGamer" class="field-help">
                {{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.URL.HELP_AIGAMER')
                }}
              </p>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>
                  {{
                    isAIGamer
                      ? t(
                          'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.MEDIA_TYPE.LABEL_AIGAMER'
                        )
                      : t(
                          'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.MEDIA_TYPE.LABEL_DEFAULT'
                        )
                  }}
                </label>
                <select v-model="formData.media_type" class="form-input">
                  <option
                    v-for="opt in mediaTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <p v-if="isAIGamer" class="field-help">
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.MEDIA_TYPE.HELP_AIGAMER'
                    )
                  }}
                </p>
              </div>
              <div class="form-group">
                <label>
                  {{
                    isAIGamer
                      ? t(
                          'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.FOLDER.LABEL_AIGAMER'
                        )
                      : t(
                          'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.FOLDER.LABEL_DEFAULT'
                        )
                  }}
                </label>
                <input
                  v-model="formData.folder_name"
                  type="text"
                  :placeholder="
                    isAIGamer
                      ? t(
                          'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.FOLDER.PLACEHOLDER_AIGAMER'
                        )
                      : t(
                          'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.FOLDER.PLACEHOLDER_DEFAULT'
                        )
                  "
                  class="form-input"
                />
                <p v-if="isAIGamer" class="field-help">
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.FOLDER.HELP_AIGAMER'
                    )
                  }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <label>
                {{
                  isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.DESCRIPTION.LABEL_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.DESCRIPTION.LABEL_DEFAULT'
                      )
                }}
              </label>
              <textarea
                v-model="formData.description"
                :placeholder="
                  isAIGamer
                    ? t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.DESCRIPTION.PLACEHOLDER_AIGAMER'
                      )
                    : t(
                        'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.DESCRIPTION.PLACEHOLDER_DEFAULT'
                      )
                "
                class="form-input form-textarea"
                rows="3"
              />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.COUNTRY.LABEL')
                }}</label>
                <select v-model="formData.country" class="form-input">
                  <option
                    v-for="opt in countryOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <p v-if="isAIGamer" class="field-help">
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.COUNTRY.HELP_AIGAMER'
                    )
                  }}
                </p>
              </div>
              <div class="form-group">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.LANGUAGE.LABEL')
                }}</label>
                <select v-model="formData.language" class="form-input">
                  <option
                    v-for="opt in languageOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <p v-if="isAIGamer" class="field-help">
                  {{
                    t(
                      'AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.LANGUAGE.HELP_AIGAMER'
                    )
                  }}
                </p>
              </div>
            </div>
            <!-- Preview -->
            <div v-if="formData.url" class="preview-section">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.PREVIEW.LABEL')
              }}</label>
              <div class="preview-box">
                <img
                  v-if="formData.media_type === 'image'"
                  :src="formData.url"
                  :alt="
                    t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.PREVIEW.ALT')
                  "
                />
                <video v-else :src="formData.url" controls />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button
              class="btn-save"
              :disabled="!canSaveDraft"
              @click="handleSave"
            >
              <span
                v-if="isSaving"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span>
                {{
                  editingItem
                    ? t('AILAB.FORM.UPDATE')
                    : t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.ACTIONS.ADD')
                }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.media-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 80px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--wlab-text-muted);
  border-radius: 24px;
  transition: 0.3s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider) {
  background: rgba(0, 102, 255, 0.25);
  border-color: rgba(0, 102, 255, 0.6);
}

:global(body.dark .toggle-switch input:checked + .toggle-slider) {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
  background: #ffffff;
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider::before) {
  background: #0066ff;
}

:global(body.dark .toggle-switch input:checked + .toggle-slider::before) {
  background: #ffffff;
}

.toggle-label {
  font-size: 14px;
  color: var(--wlab-text-secondary);
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  height: 36px;
  box-sizing: border-box;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  cursor: pointer;
}

.filter-select option {
  background: var(--wlab-bg-primary);
  color: var(--wlab-text-primary);
}

.refresh-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  color: var(--wlab-text-primary);
  cursor: pointer;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  height: 36px;
  box-sizing: border-box;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  color: #000000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.view-toggle {
  display: flex;
  gap: 2px;
  background: var(--wlab-bg-tertiary);
  border-radius: 6px;
  padding: 2px;
}

.view-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.view-btn.active {
  background: rgba(255, 255, 255, 0.2);
  color: #0066ff;
}

/* Media Container */
.media-container {
  position: relative;
  padding: 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  min-height: 200px;
}

.media-container.is-default {
  opacity: 0.8;
}

.default-watermark {
  position: absolute;
  right: 3%;
  bottom: 5%;
  font-size: 5rem;
  font-weight: 700;
  color: var(--wlab-border-light);
  pointer-events: none;
  z-index: 1;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  min-height: 200px;
}

.empty-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(0, 102, 255, 0.15) 0%,
    rgba(0, 102, 255, 0.05) 100%
  );
  border: 1px solid rgba(0, 102, 255, 0.2);
  color: #0066ff;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  margin: 0 0 8px 0;
}

.empty-text {
  font-size: 14px;
  color: var(--wlab-text-muted);
  margin: 0;
}

.add-first-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  margin-top: 16px;
  background: #ffffff;
  border: none;
  border-radius: 8px;
  color: #000000;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-first-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

/* Grid View */
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  position: relative;
  z-index: 2;
}

.media-card {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s;
}

.media-card:hover {
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.card-header {
  padding: 12px;
  border-bottom: 1px solid var(--wlab-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.folder-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--wlab-text-secondary);
}

.folder-name {
  font-size: 13px;
  font-weight: 600;
}

.card-actions,
.row-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.edit {
  color: var(--wlab-text-secondary);
}

.action-btn.edit:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.action-btn.delete {
  color: var(--wlab-text-muted);
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

.card-content {
  padding: 12px;
  display: flex;
  gap: 12px;
}

.media-preview {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.preview-image,
.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  color: var(--wlab-text-primary);
  cursor: pointer;
}

.media-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.media-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-primary);
  margin: 0 0 4px 0;
}

.media-desc {
  font-size: 12px;
  color: var(--wlab-text-secondary);
  margin: 0 0 auto 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.media-dates {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
}

.media-dates small {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

/* List View */
.media-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.media-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 8px;
  transition: all 0.2s;
}

.media-row:hover {
  background: var(--wlab-border-light);
}

.media-thumb {
  width: 60px;
  height: 60px;
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.media-thumb img,
.media-thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--wlab-text-primary);
  background: rgba(255, 255, 255, 0.8);
  padding: 4px;
  border-radius: 50%;
}

.media-details {
  flex: 1;
  min-width: 0;
}

.media-details .folder-info {
  font-size: 12px;
  margin-bottom: 4px;
}

.media-details .media-title {
  font-size: 14px;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-date {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

.row-actions {
  flex-shrink: 0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: var(--wlab-bg-primary, #1a1a1a);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--wlab-text-primary);
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
}

.required {
  color: #ef4444;
}

.field-help {
  font-size: 12px;
  color: var(--wlab-text-muted);
  line-height: 1.4;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-input {
  padding: 10px 12px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input, #2a2a2a);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #ffffff;
}

.form-input::placeholder {
  color: var(--wlab-text-muted);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-section label {
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
}

.preview-box {
  width: 100%;
  max-height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.preview-box img,
.preview-box video {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--wlab-border-light);
}

.btn-cancel {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.05);
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* -------------------------------------------------------------------------- */
/* UI redesign overrides (no @apply; keep stylelint happy)                     */
/* -------------------------------------------------------------------------- */

.media-tab {
  width: 100%;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid var(--wlab-border-light);
  background: var(--wlab-bg-tertiary);
}

.card-content {
  margin-top: 16px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--wlab-border-light);
  background: var(--wlab-bg-tertiary);
  color: var(--wlab-text-primary);
  transition: all 0.2s ease;
}

.action-btn:hover {
  filter: brightness(1.05);
}

.action-btn.delete {
  color: var(--wlab-text-error);
}

.form-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid var(--wlab-border-light);
  background: var(--wlab-bg-primary);
  color: var(--wlab-text-primary);
  font-size: 14px;
  outline: none;
}

.form-input:focus {
  border-color: var(--wlab-border-default);
}
</style>
